import React from 'react'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import { formatDate, getFlagPath } from '../../Functions/Utilities'

// Helper function to determine if content should be RTL
const isRTL = (nationality) => {
  if (!nationality || !nationality.data || !nationality.data.attributes) return false;
  // Prefer explicit is_rtl boolean if available
  if (typeof nationality.data.attributes.is_rtl === 'boolean') {
    return nationality.data.attributes.is_rtl;
  }
  // Fallback to language_query
  const rtlLangs = ['ar', 'fa', 'he', 'ur', 'ps', 'dv', 'ku', 'yi'];
  const lang = nationality.data.attributes.language_query?.toLowerCase();
  return rtlLangs.includes(lang);
};

const PostCard = ({ post, postType }) => {
  const { id, attributes } = post
  const {
    title,
    description,
    createdAt,
    picture,
    post_type,
    price,
    start,
    end,
    is_imigo_partner,
    nationality
  } = attributes

  // Determine if the content should be RTL
  const isRtl = isRTL(nationality);

  // Get the flag path for the nationality
  const flagPath = getFlagPath(nationality);

  // Get the post type if not provided
  const type = postType || (post_type?.data?.attributes?.name || 'Article')
  
  // Format the description by removing HTML tags for preview
  const stripHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return doc.body.textContent || ''
  }
  
  const getImageUrl = () => {
    if (picture?.data?.attributes?.formats?.medium?.url) {
      return `https://panel.imigo.app${picture.data.attributes.formats.medium.url}`
    }
    if (picture?.data?.attributes?.url) {
      return `https://panel.imigo.app${picture.data.attributes.url}`
    }
    return 'https://panel.imigo.app/uploads/medium_logo_baf7005f31.jpg'
  }

  const handleImageError = (e) => {
    e.target.src = 'https://panel.imigo.app/uploads/medium_logo_baf7005f31.jpg'
  }

  // Generate the post URL based on type and ID
  const getPostUrl = () => {
    // Check if slug exists, otherwise generate one from title
    const slug = attributes.slug || (title ? title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w\-]+/g, '') : id);
    let postTypeUrl = type.toLowerCase();
    
    // Ensure we use plural forms for post types
    if (postTypeUrl === 'article') postTypeUrl = 'articles'
    else if (postTypeUrl === 'business') postTypeUrl = 'businesses'
    else if (postTypeUrl === 'event') postTypeUrl = 'events'
    else if (postTypeUrl === 'activity') postTypeUrl = 'activities'
    
    return `/${postTypeUrl}/${slug}`
  }

  // Get a short description for the card
  const getShortDescription = () => {
    const strippedText = stripHtml(description)
    return strippedText.length > 150 ? `${strippedText.substring(0, 150)}...` : strippedText
  }

  return (
    <div className={`post-card bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 ${isRtl ? 'rtl' : 'ltr'}`}>
      <Link to={getPostUrl()} className="block">
        <div className="post-image h-[200px] overflow-hidden relative">
          <img 
            src={getImageUrl()} 
            alt={title} 
            className="w-full h-full object-cover"
            onError={handleImageError}
          />
          {is_imigo_partner && (
            <div className="absolute top-2 right-2 bg-neonorange text-white text-xs px-2 py-1 rounded-full">
              Official Partner
            </div>
          )}
          {flagPath && (
            <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm rounded-full p-1 shadow-md">
              <img 
                src={flagPath} 
                alt="Nationality flag" 
                className="w-6 h-6 object-cover rounded-full"
                onError={(e) => {
                  e.target.style.display = 'none'
                }}
              />
            </div>
          )}
          {type && (
            <div className={`absolute ${flagPath ? 'top-10 left-2' : 'top-2 left-2'} bg-darkgray text-white text-xs px-2 py-1 rounded-full`}>
              {type}
            </div>
          )}
        </div>
      </Link>
      <div className="post-content p-4">
        <Link to={getPostUrl()} className="block">
          <h3 className={`text-lg font-bold mb-2 hover:text-neonorange transition-colors duration-300 ${isRtl ? 'text-right' : 'text-left'}`}>{title}</h3>
        </Link>
        <p className={`text-sm text-gray-600 mb-3 ${isRtl ? 'text-right' : 'text-left'}`}>{getShortDescription()}</p>
        
        <div className={`post-meta flex justify-between items-center text-xs text-gray-500 ${isRtl ? 'flex-row-reverse' : ''}`}>
          <div className="post-date">
            {formatDate(createdAt)}
          </div>
          
          {/* Show date for events */}
          {start && (
            <div className="post-event-date">
              {new Date(start).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

PostCard.propTypes = {
  post: PropTypes.object.isRequired,
  postType: PropTypes.string
}

export default PostCard 